/**
 * Estimates how much VAT a person pays back out of their NET (after-tax) income,
 * as a function of income level. Built for the "Tax Iceberg" calculator: net pay
 * looks final, but a chunk of it comes straight back to the state via VAT the
 * moment it's spent.
 *
 * ---------------------------------------------------------------------------
 * SOURCES (checked directly, not via third-party VAT-compliance blogs)
 * ---------------------------------------------------------------------------
 *
 * 1. VAT rates, vero.fi, accessed 2026-09-05:
 *    https://www.vero.fi/en/businesses-and-corporations/taxes-and-charges/vat/rates-of-vat/
 *    - General rate 25.5% (since 1 Sep 2024): most goods/services.
 *    - Reduced rate 13.5% (lowered from 14% on 1 Jan 2026): groceries/food,
 *      animal feed, restaurant & catering services, books, pharmaceuticals,
 *      sports/fitness & cultural/entertainment admission, passenger transport
 *      (taxi/bus/train/domestic flights), accommodation, public broadcasting
 *      (moved here from 10% in 2026).
 *    - Reduced rate 10%: newspapers and magazines only (the only thing left at
 *      this rate after public broadcasting moved to 13.5% in 2026).
 *    Corroborated against a second Finnish source (Greenstep, a Finnish
 *    accounting/tax advisory, "Verokannat muuttuvat jälleen 1.1.2026",
 *    https://greenstep.fi/artikkelit/alv-kannat-muuttuvat-jalleen-1-1-2026/,
 *    accessed 2026-09-05) which lists the same category split.
 *    Certain supplies are VAT-EXEMPT (0%, not just "reduced"), notably:
 *    residential rent, most healthcare services, education, and financial/
 *    insurance services (Arvonlisäverolaki, general EU VAT exemption
 *    categories): this matters below because a naive "rent + healthcare are
 *    taxed at 25.5%" assumption would meaningfully overstate low-income VAT
 *    burden, since rent is such a large share of low-income spending.
 *
 * 2. Statistics Finland (Tilastokeskus) Household Consumption Survey
 *    (kulutustutkimus), 2022 wave (most recent; conducted every ~4-6 years),
 *    queried directly from the public PxWeb statistical database API,
 *    accessed 2026-09-05:
 *    - https://pxdata.stat.fi/PXWeb/api/v1/fi/StatFin/ktutk/14pg.px
 *      "Kotitalouksien kulutusmenot tuloviidenneksittäin" (household
 *      consumption expenditure by income quintile): used for total spending
 *      and the COICOP-12 consumption-category breakdown, plus a handful of
 *      COICOP sub-items (actual vs. imputed rent, mortgage interest, medicine
 *      vs. other health services, passenger-transport services, books,
 *      newspapers/magazines) needed to separate exempt/reduced-rate spending
 *      from standard-rate spending within those categories.
 *    - https://pxdata.stat.fi/PXWeb/api/v1/fi/StatFin/ktutk/14qa.px
 *      "Taustatiedot tuloviidenneksen mukaan" (background data by income
 *      quintile): used for household disposable income (Kaytetmk_1_mean) and
 *      average household size in persons (jlkm_mean, "Kotitalouden
 *      keskikoko"), needed to convert household-level income into a literal
 *      per-person income scale (see modeling choice 1 below).
 *    Both tables are official StatFin/PxWeb output, not scraped from a
 *    write-up: the numbers below are the raw API response values.
 *    Human-readable summary pages consulted for corroboration/context:
 *    https://stat.fi/julkaisu/clmyi6vct7jjp0cutgh4ngdsb (accessed 2026-09-05).
 *
 * A prior research pass flagged the VAT rates as needing primary-source
 * verification and the quintile-level spending-by-category split as
 * "maybe not accessible". Both were in fact accessible directly: the vero.fi
 * VAT-rates page confirmed the rates above, and the PxWeb API (a public,
 * un-authenticated JSON endpoint) returned the full quintile x COICOP-category
 * matrix, plus the sub-category splits needed to isolate exempt items.
 * Nothing in this file is a guessed or invented number; every figure in the
 * DATA_POINTS-derivation table below is either a direct PxWeb query result or
 * an arithmetic combination of two direct query results (documented inline).
 *
 * ---------------------------------------------------------------------------
 * WHAT'S REAL DATA vs. OUR OWN MODELING CHOICES
 * ---------------------------------------------------------------------------
 *
 * Real data (5 points, one per income quintile, 2022, the finest breakdown
 * Statistics Finland publishes: quintiles, not deciles):
 *   - Household disposable income (Kaytetmk_1_mean)
 *   - Household average size in persons (jlkm_mean, "Kotitalouden keskikoko")
 *   - Household consumption expenditure, both in total and split across the
 *     12 standard COICOP categories, plus 7 targeted sub-categories (actual
 *     rent 041, imputed rent 042, mortgage interest 046, medicines 061,
 *     passenger transport services 073, books 0971, newspapers/magazines 0972)
 *
 * Our own modeling choices (clearly a simplification, not survey output):
 *   1. Per-person scaling: the survey reports household-level euros. To
 *      compare against a single person's entered net income, we divide
 *      household disposable income by that quintile's jlkm_mean (its average
 *      number of persons per household — a real PxWeb field, same 14qa.px
 *      table as Kaytetmk_1_mean, contentscode "jlkm_mean"), giving a literal
 *      per-person income figure. (An earlier version of this file used
 *      oecdmod_mean, the OECD-modified "consumption unit" scale — first
 *      adult=1, other adults=0.5, children=0.3 — which is an economies-of-
 *      scale-adjusted concept, not a literal per-person one; switched to
 *      jlkm_mean since the calculator asks for one individual's own salary,
 *      not an equivalized-household figure.) vatLiableSharePct and
 *      effectiveVatRatePct themselves don't depend on this choice at all —
 *      they're each quintile's own raw household spending/income ratio,
 *      computed before any per-person division — so switching the divisor
 *      only rescales the x-axis (income) the 5 points sit on, i.e. where a
 *      given net income lands for interpolation, not the values interpolated.
 *   2. Category -> VAT-rate mapping. The COICOP consumption categories don't
 *      map 1:1 onto VAT rates, so *we* assigned a rate to each category/
 *      sub-category based on the vero.fi list above:
 *        - Food (01), medicines (061), passenger transport services (073),
 *          books (0971), restaurants & hotels (11): 13.5%
 *        - Newspapers/magazines (0972): 10%
 *        - Actual rent (041), mortgage interest (046), non-medicine health
 *          services (062-064, i.e. category 06 minus medicines), and
 *          education (10): 0% (VAT-exempt supplies)
 *        - Everything else (alcohol/tobacco, clothing, the taxable remainder
 *          of housing i.e. electricity/water/maintenance, furnishings, the
 *          non-passenger-transport-service remainder of transport,
 *          communication, the remainder of recreation/culture, misc. goods
 *          & services): standard 25.5%
 *      Simplification within that: categories 09 (recreation/culture) and 12
 *      (miscellaneous) contain small amounts of other reduced-rate or exempt
 *      items we didn't separately query (e.g. sports/cultural admission fees
 *      buried in category 09, insurance/financial services buried in
 *      category 12) and which are therefore folded into the 25.5% bucket.
 *      Both categories are a modest share of total spending, so this is a
 *      minor, disclosed overstatement of the true VAT burden, not a material
 *      one.
 *   3. Imputed rent (COICOP 042, the notional "rent" Statistics Finland
 *      assigns to owner-occupiers for national-accounts comparability) is
 *      excluded entirely from "spending" here: no cash actually changes
 *      hands for it, so it can't have any VAT attached to it, and including
 *      it would overstate how much of their real income higher-income
 *      (more often owner-occupier) households "spend rather than save".
 *   4. effectiveVatRatePct is the share of what was actually spent (at
 *      VAT-inclusive retail prices, which is how both vero.fi's rates and
 *      Statistics Finland's consumption figures are quoted) that was tax.
 *      For a rate r quoted the normal ("net price x (1+r)") way, that share
 *      is r/(1+r), not r itself: e.g. at the 25.5% standard rate, VAT is
 *      25.5/125.5 = 20.3% of what you actually paid, not 25.5% of it. Each
 *      category's rate is converted this way before blending, so
 *      effectiveVatRatePct x vatLiableSharePct x income reproduces actual
 *      euros of VAT paid, not an inflated "rate-on-top-of-itself" figure.
 *   5. Disposable income (kaytettavissa olevat tulot) is a broader concept
 *      than take-home wages alone: it nets out taxes but adds back cash
 *      transfers (child benefit, housing allowance, etc). We treat a user's
 *      entered net wage income as a stand-in for this, which is a reasonable
 *      approximation for most working-age single earners but will understate
 *      real disposable income (and therefore overstate vatLiableSharePct
 *      somewhat) for households that also receive meaningful transfer income.
 *
 * ---------------------------------------------------------------------------
 * DERIVATION TABLE (2022 survey year; all euros are annual)
 * ---------------------------------------------------------------------------
 *
 * Quintile                                    I        II       III      IV       V
 * Household disposable income                18,796   33,041   44,550   57,642   97,410
 * Household size, persons (jlkm_mean)          1.43     1.86     1.97     2.04     2.09
 * -> Per-person income (income / jlkm_mean)  13,144   17,764   22,614   28,256   46,608
 *
 * Sum of the 12 COICOP categories            18,840   28,420   36,080   43,221   59,171
 * less imputed rent (042)                       -907    -3,407   -5,685   -8,235  -12,140
 * = spending used here ("taxable base")      17,933   25,013   30,395   34,986   47,031
 *
 * VAT embedded in that spending (categories
 * weighted by the rate mapping above, VAT
 * backed out of each VAT-inclusive amount)    2,037    3,533    4,668    5,584    7,857
 *
 * vatLiableSharePct = taxable base /
 *   household disposable income (unaffected
 *   by the per-person division, see note 1
 *   above)                                    95.41%   75.70%   68.23%   60.70%   48.28%
 * effectiveVatRatePct = VAT embedded /
 *   taxable base                              11.36%   14.12%   15.36%   15.96%   16.70%
 *
 * (Note: the sum of the 12 published category subtotals doesn't exactly
 * match Statistics Finland's separately published grand total for total
 * consumption expenditure: e.g. 18,840 vs. a published ~19,659 for quintile
 * I. This ~1-4% gap is normal survey noise: each cell is an independently
 * weighted sample estimate, so subtotals don't sum perfectly to the topline
 * figure. We use the sum of the categories we actually categorized by VAT
 * rate, so our percentages are internally consistent with each other, at the
 * cost of a small, disclosed deviation from Statistics Finland's own headline
 * total.)
 *
 * Both series move the direction you'd expect and validate against the
 * general "VAT is regressive" finding in the literature even after this
 * exemption-aware treatment: VAT paid as a share of income comes out at
 * roughly 10.8% (quintile I) declining to 8.1% (quintile V) once you combine
 * the two effects, even though the *effective rate on what's spent* actually
 * rises with income (poorer households spend proportionally more on exempt
 * rent and reduced-rate food; richer households spend proportionally more on
 * standard-rate durables, vehicles and travel).
 *
 * ---------------------------------------------------------------------------
 * INTERPOLATION
 * ---------------------------------------------------------------------------
 * The 5 quintile points above are real (well, real-derived) data. Everything
 * *between* them is our own smoothing, not survey data: we run a monotone
 * cubic Hermite interpolation (Fritsch-Carlson method) through the 5 points,
 * which is smooth (continuously differentiable) like a Catmull-Rom spline
 * but, unlike a plain Catmull-Rom, is mathematically guaranteed not to
 * overshoot and violate monotonicity between control points. That matters
 * here because vatLiableSharePct must never tick up as income rises (savings
 * propensity rising with income is the whole point of the model), which a
 * naive cubic spline is not guaranteed to respect. Below the lowest quintile
 * or above the highest, we hold the value flat at that quintile's figure
 * rather than extrapolating: we have no survey data out there, and
 * extrapolating a cubic can produce unrealistic (even negative) numbers,
 * especially towards very low incomes.
 */

interface QuintileDataPoint {
  /** Per-person annual disposable income, EUR (household disposable income / jlkm_mean). */
  incomeAnnual: number;
  /** % of income assumed spent rather than saved. */
  vatLiableSharePct: number;
  /** Blended VAT rate as a share of what was actually spent (VAT-inclusive basis). */
  effectiveVatRatePct: number;
}

// 2022 Statistics Finland household consumption survey, by income quintile.
// See the derivation table in the header comment for how these were computed.
const QUINTILE_DATA_POINTS: QuintileDataPoint[] = [
  { incomeAnnual: 13_144, vatLiableSharePct: 95.41, effectiveVatRatePct: 11.36 }, // I (lowest 20%)
  { incomeAnnual: 17_764, vatLiableSharePct: 75.70, effectiveVatRatePct: 14.12 }, // II
  { incomeAnnual: 22_614, vatLiableSharePct: 68.23, effectiveVatRatePct: 15.36 }, // III
  { incomeAnnual: 28_256, vatLiableSharePct: 60.70, effectiveVatRatePct: 15.96 }, // IV
  { incomeAnnual: 46_608, vatLiableSharePct: 48.28, effectiveVatRatePct: 16.70 }, // V (highest 20%)
];

export interface VatBurdenEstimate {
  vatLiableSharePct: number; // % of net income assumed spent (not saved)
  effectiveVatRatePct: number; // blended VAT rate applied to that spending
  vatPaid: number; // euros
}

export function estimateVatBurden(netAnnualIncome: number): VatBurdenEstimate {
  if (!(netAnnualIncome > 0)) throw new Error("Provide a positive netAnnualIncome");

  const incomes = QUINTILE_DATA_POINTS.map((p) => p.incomeAnnual);
  const shares = QUINTILE_DATA_POINTS.map((p) => p.vatLiableSharePct);
  const rates = QUINTILE_DATA_POINTS.map((p) => p.effectiveVatRatePct);

  // Interpolation (and clamping outside the survey's income range) happens on
  // the model's x-axis; vatPaid below is still computed against the caller's
  // real, unclamped income.
  const vatLiableSharePct = monotoneCubicInterpolate(incomes, shares, netAnnualIncome);
  const effectiveVatRatePct = monotoneCubicInterpolate(incomes, rates, netAnnualIncome);
  const vatPaid = netAnnualIncome * (vatLiableSharePct / 100) * (effectiveVatRatePct / 100);

  const r = (x: number) => Math.round(x * 100) / 100;
  return {
    vatLiableSharePct: r(vatLiableSharePct),
    effectiveVatRatePct: r(effectiveVatRatePct),
    vatPaid: r(vatPaid),
  };
}

/**
 * Monotone cubic Hermite interpolation (Fritsch-Carlson method).
 *
 * Produces a smooth (C1-continuous) curve through monotonic input data
 * without the overshoot a plain cubic/Catmull-Rom spline can introduce
 * between control points. `xs` must be sorted ascending. Values outside the
 * [xs[0], xs[last]] range are clamped to the nearest endpoint's y value
 * rather than extrapolated.
 */
function monotoneCubicInterpolate(xs: number[], ys: number[], x: number): number {
  const n = xs.length;
  if (x <= xs[0]) return ys[0];
  if (x >= xs[n - 1]) return ys[n - 1];

  // Secant slopes between consecutive points.
  const d: number[] = [];
  for (let i = 0; i < n - 1; i++) d.push((ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]));

  // Initial tangents: average of adjacent secants (endpoints just take the
  // one adjacent secant they have).
  const m: number[] = new Array(n);
  m[0] = d[0];
  m[n - 1] = d[n - 2];
  for (let i = 1; i < n - 1; i++) m[i] = (d[i - 1] + d[i]) / 2;

  // Fritsch-Carlson adjustment: shrink tangents as needed so the cubic on
  // each interval can't overshoot past monotonic (never grows a tangent, so
  // adjustments made for one interval can't undo another interval's fix).
  for (let i = 0; i < n - 1; i++) {
    if (d[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const alpha = m[i] / d[i];
    const beta = m[i + 1] / d[i];
    const magnitudeSquared = alpha * alpha + beta * beta;
    if (magnitudeSquared > 9) {
      const tau = 3 / Math.sqrt(magnitudeSquared);
      m[i] = tau * alpha * d[i];
      m[i + 1] = tau * beta * d[i];
    }
  }

  // Locate the interval containing x, then evaluate the Hermite cubic on it.
  let i = 0;
  while (i < n - 2 && x > xs[i + 1]) i++;

  const h = xs[i + 1] - xs[i];
  const t = (x - xs[i]) / h;
  const t2 = t * t;
  const t3 = t2 * t;

  const h00 = 2 * t3 - 3 * t2 + 1;
  const h10 = t3 - 2 * t2 + t;
  const h01 = -2 * t3 + 3 * t2;
  const h11 = t3 - t2;

  return h00 * ys[i] + h10 * h * m[i] + h01 * ys[i + 1] + h11 * h * m[i + 1];
}
